import React from 'react';
import { t } from '@lingui/macro';
import { SearchInput, Button, Dialog, WindowedTable as Table, SortRule } from '@economic/taco';
import { useRouteAuthorisation } from '@economic/react-app';
import { AccountMultipleDimension } from '~/types/internal-api';
import CreateAccountForm from '../forms/Accounts/CreateAccount';
import { ViewProps } from './types';
import View, { useViewFilter } from '../shared/components/View/View';
import { useTableSortPreferences } from '../shared/hooks/useTableSortPreference';
import { AccountType, AccountTypeNumber, VatAccount } from '../../types';
import { useAccounts } from '../shared/hooks/masterdata/useAccounts';

const HEADING_ACCOUNT_TYPES = [AccountTypeNumber.Heading, AccountTypeNumber.HeadingBreak];

const isHeading = (accountTypeNumber: AccountTypeNumber) => HEADING_ACCOUNT_TYPES.includes(accountTypeNumber);

export interface DepartmentModel {
    departmentNumber: number;
    name: string;
    access: boolean;
}
// Suffixed "Model" because there is a DOM APIs Account interface
export interface AccountModel {
    access: boolean;
    balance: number;
    isBlockedForDirectEntries: boolean;
    isDefaultCredit: boolean;
    isDepartmentMandatory: boolean;
    isUnitMandatory: boolean;
    name: string;
    number: number;
    type: AccountType;
    department: DepartmentModel;
    dimensions: AccountMultipleDimension[];
    contraAccount: AccountModel;
    vatAccount: VatAccount;
    currency: string;
}

interface Props extends ViewProps<AccountModel> {
    isBlockedForDirectEntries?: boolean;
    selectedAccountNumber?: number;
    modalDraggable?: boolean;
    showAllAccountTypes?: boolean;
    showAllAccounts?: boolean;
    allowHeadingClick?: boolean;
}

const findActiveIndex = (accounts: AccountModel[], selectedAccountNumber?: number) => {
    const activeIndex = accounts?.findIndex(account => account.number === selectedAccountNumber);
    return activeIndex >= 0 ? activeIndex : undefined;
};

const Accounts = ({
    isBlockedForDirectEntries,
    onCreated: handleCreated,
    onSelected: handleSelected,
    selectedAccountNumber,
    modalDraggable,
    showAllAccountTypes = false,
    showAllAccounts = false,
    allowHeadingClick = false,
}: Props) => {
    function showLedger(accountNumber: number) {
        window.top?.PopUp2(`/generelt/dataedit.asp?popup=yes&form=8&skjultrans=1&kladde=1&kontonr=${accountNumber}`, 820, 400);
    }

    const [filter, setFilter, filterBy] = useViewFilter();
    const tableSort = useTableSortPreferences('accounts');
    const [hasJournalAccess] = useRouteAuthorisation('/accounting');

    const { data: accounts, refetch } = useAccounts({
        showAllAccounts,
        showAllAccountTypes,
        showHeadingAccounts: true,
        isBlockedForDirectEntries,
    });

    const [activeIndex, setActiveIndex] = React.useState(findActiveIndex(accounts, selectedAccountNumber));

    const onSort = (sortPreferences: SortRule<any>[] | undefined, sortedCollection?: AccountModel[]) => {
        if (tableSort.onSort) {
            tableSort.onSort(sortPreferences);
        }
        const newIndex = findActiveIndex(sortedCollection ?? [], selectedAccountNumber);
        setActiveIndex(newIndex);
    };

    return React.useMemo(
        () => (
            <View>
                <View.Title>{t`Chart of accounts`}</View.Title>

                <View.Header>
                    <View.Header.Actions>
                        <Button
                            appearance="primary"
                            dialog={props => (
                                <Dialog size="md" draggable={modalDraggable} {...props}>
                                    <Dialog.Content aria-label={t`FORM_ACCOUNTS_CREATETEXT`}>
                                        {({ close }) => (
                                            <CreateAccountForm
                                                modalDraggable={modalDraggable}
                                                onCancel={close}
                                                onSubmitted={account => {
                                                    refetch();
                                                    if (handleCreated) {
                                                        handleCreated(account);
                                                    }
                                                }}
                                            />
                                        )}
                                    </Dialog.Content>
                                </Dialog>
                            )}>
                            {t`New account`}
                        </Button>
                    </View.Header.Actions>
                    <View.Header.Filters>
                        <SearchInput
                            autoFocus
                            onChange={event => {
                                setActiveIndex(0);
                                setFilter(event.target.value);
                            }}
                            placeholder={t`Search for no. or name`}
                            value={filter}
                        />
                    </View.Header.Filters>
                </View.Header>

                <View.Content>
                    <Table<AccountModel>
                        {...tableSort}
                        data={accounts.filter(account => filterBy(account.number) || filterBy(account.name))}
                        onRowClick={row => {
                            if (!isHeading(row.values.type.number) || allowHeadingClick) {
                                handleSelected(row.values);
                            }
                        }}
                        actions={
                            hasJournalAccess
                                ? [
                                      {
                                          icon: 'entry-type-customer-invoice',
                                          text: t`Ledger card`,
                                          onClick: row => showLedger(row.values.number),
                                          tooltip: t`Ledger card`,
                                          showOnRow: true,
                                          visible: row => !isHeading(row.values.type.number),
                                      },
                                  ]
                                : []
                        }
                        rowClassName={row => {
                            if (isHeading(row.values.type.number)) {
                                if (allowHeadingClick) {
                                    return 'font-bold';
                                }
                                return 'font-bold pointer-events-none';
                            }

                            return undefined;
                        }}
                        dangerouslyHijackGlobalKeyboardNavigation
                        activeIndex={activeIndex}
                        onChangeActiveIndex={setActiveIndex}
                        onSort={onSort}>
                        <Table.Column<AccountModel> accessor="number" headRenderer={t`No.`} flex="0 0 75px" sort="asc" />
                        <Table.Column<AccountModel> accessor="name" align="left" headRenderer={t`Name`} />
                        <Table.Column<AccountModel> accessor="type.name" flex="0 0 150px" />
                    </Table>
                </View.Content>
            </View>
        ),
        [refetch, accounts, filter, activeIndex]
    );
};

export default Accounts;
